"use strict";
/**
 * @module resample
 */

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.resampleInterleaved = exports.resampleBilinearInterleaved = exports.resampleNearestInterleaved = exports.resample = exports.resampleBilinear = exports.resampleNearest = void 0;

function copyNewSize(array, width, height) {
  var samplesPerPixel = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 1;
  return new (Object.getPrototypeOf(array).constructor)(width * height * samplesPerPixel);
}
/**
 * Resample the input arrays using nearest neighbor value selection.
 * @param {TypedArray[]} valueArrays The input arrays to resample
 * @param {number} inWidth The width of the input rasters
 * @param {number} inHeight The height of the input rasters
 * @param {number} outWidth The desired width of the output rasters
 * @param {number} outHeight The desired height of the output rasters
 * @returns {TypedArray[]} The resampled rasters
 */


function resampleNearest(valueArrays, inWidth, inHeight, outWidth, outHeight) {
  var relX = inWidth / outWidth;
  var relY = inHeight / outHeight;
  return valueArrays.map(function (array) {
    var newArray = copyNewSize(array, outWidth, outHeight);

    for (var y = 0; y < outHeight; ++y) {
      var cy = Math.min(Math.round(relY * y), inHeight - 1);

      for (var x = 0; x < outWidth; ++x) {
        var cx = Math.min(Math.round(relX * x), inWidth - 1);
        var value = array[cy * inWidth + cx];
        newArray[y * outWidth + x] = value;
      }
    }

    return newArray;
  });
}

exports.resampleNearest = resampleNearest; // simple linear interpolation, code from:
// https://en.wikipedia.org/wiki/Linear_interpolation#Programming_language_support

function lerp(v0, v1, t) {
  return (1 - t) * v0 + t * v1;
}
/**
 * Resample the input arrays using bilinear interpolation.
 * @param {TypedArray[]} valueArrays The input arrays to resample
 * @param {number} inWidth The width of the input rasters
 * @param {number} inHeight The height of the input rasters
 * @param {number} outWidth The desired width of the output rasters
 * @param {number} outHeight The desired height of the output rasters
 * @returns {TypedArray[]} The resampled rasters
 */


function resampleBilinear(valueArrays, inWidth, inHeight, outWidth, outHeight) {
  var relX = inWidth / outWidth;
  var relY = inHeight / outHeight;
  return valueArrays.map(function (array) {
    var newArray = copyNewSize(array, outWidth, outHeight);

    for (var y = 0; y < outHeight; ++y) {
      var rawY = relY * y;
      var yl = Math.floor(rawY);
      var yh = Math.min(Math.ceil(rawY), inHeight - 1);

      for (var x = 0; x < outWidth; ++x) {
        var rawX = relX * x;
        var tx = rawX % 1;
        var xl = Math.floor(rawX);
        var xh = Math.min(Math.ceil(rawX), inWidth - 1);
        var ll = array[yl * inWidth + xl];
        var hl = array[yl * inWidth + xh];
        var lh = array[yh * inWidth + xl];
        var hh = array[yh * inWidth + xh];
        var value = lerp(lerp(ll, hl, tx), lerp(lh, hh, tx), rawY % 1);
        newArray[y * outWidth + x] = value;
      }
    }

    return newArray;
  });
}

exports.resampleBilinear = resampleBilinear;
/**
 * Resample the input arrays using the selected resampling method.
 * @param {TypedArray[]} valueArrays The input arrays to resample
 * @param {number} inWidth The width of the input rasters
 * @param {number} inHeight The height of the input rasters
 * @param {number} outWidth The desired width of the output rasters
 * @param {number} outHeight The desired height of the output rasters
 * @param {string} [method = 'nearest'] The desired resampling method
 * @returns {TypedArray[]} The resampled rasters
 */

function resample(valueArrays, inWidth, inHeight, outWidth, outHeight) {
  var method = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : 'nearest';

  switch (method.toLowerCase()) {
    case 'nearest':
      return resampleNearest(valueArrays, inWidth, inHeight, outWidth, outHeight);

    case 'bilinear':
    case 'linear':
      return resampleBilinear(valueArrays, inWidth, inHeight, outWidth, outHeight);

    default:
      throw new Error("Unsupported resampling method: '".concat(method, "'"));
  }
}

exports.resample = resample;
/**
 * Resample the pixel interleaved input array using nearest neighbor value selection.
 * @param {TypedArray} valueArrays The input arrays to resample
 * @param {number} inWidth The width of the input rasters
 * @param {number} inHeight The height of the input rasters
 * @param {number} outWidth The desired width of the output rasters
 * @param {number} outHeight The desired height of the output rasters
 * @param {number} samples The number of samples per pixel for pixel
 *                         interleaved data
 * @returns {TypedArray} The resampled raster
 */

function resampleNearestInterleaved(valueArray, inWidth, inHeight, outWidth, outHeight, samples) {
  var relX = inWidth / outWidth;
  var relY = inHeight / outHeight;
  var newArray = copyNewSize(valueArray, outWidth, outHeight, samples);

  for (var y = 0; y < outHeight; ++y) {
    var cy = Math.min(Math.round(relY * y), inHeight - 1);

    for (var x = 0; x < outWidth; ++x) {
      var cx = Math.min(Math.round(relX * x), inWidth - 1);

      for (var i = 0; i < samples; ++i) {
        var value = valueArray[cy * inWidth * samples + cx * samples + i];
        newArray[y * outWidth * samples + x * samples + i] = value;
      }
    }
  }

  return newArray;
}

exports.resampleNearestInterleaved = resampleNearestInterleaved;
/**
 * Resample the pixel interleaved input array using bilinear interpolation.
 * @param {TypedArray} valueArrays The input arrays to resample
 * @param {number} inWidth The width of the input rasters
 * @param {number} inHeight The height of the input rasters
 * @param {number} outWidth The desired width of the output rasters
 * @param {number} outHeight The desired height of the output rasters
 * @param {number} samples The number of samples per pixel for pixel
 *                         interleaved data
 * @returns {TypedArray} The resampled raster
 */

function resampleBilinearInterleaved(valueArray, inWidth, inHeight, outWidth, outHeight, samples) {
  var relX = inWidth / outWidth;
  var relY = inHeight / outHeight;
  var newArray = copyNewSize(valueArray, outWidth, outHeight, samples);

  for (var y = 0; y < outHeight; ++y) {
    var rawY = relY * y;
    var yl = Math.floor(rawY);
    var yh = Math.min(Math.ceil(rawY), inHeight - 1);

    for (var x = 0; x < outWidth; ++x) {
      var rawX = relX * x;
      var tx = rawX % 1;
      var xl = Math.floor(rawX);
      var xh = Math.min(Math.ceil(rawX), inWidth - 1);

      for (var i = 0; i < samples; ++i) {
        var ll = valueArray[yl * inWidth * samples + xl * samples + i];
        var hl = valueArray[yl * inWidth * samples + xh * samples + i];
        var lh = valueArray[yh * inWidth * samples + xl * samples + i];
        var hh = valueArray[yh * inWidth * samples + xh * samples + i];
        var value = lerp(lerp(ll, hl, tx), lerp(lh, hh, tx), rawY % 1);
        newArray[y * outWidth * samples + x * samples + i] = value;
      }
    }
  }

  return newArray;
}

exports.resampleBilinearInterleaved = resampleBilinearInterleaved;
/**
 * Resample the pixel interleaved input array using the selected resampling method.
 * @param {TypedArray} valueArray The input array to resample
 * @param {number} inWidth The width of the input rasters
 * @param {number} inHeight The height of the input rasters
 * @param {number} outWidth The desired width of the output rasters
 * @param {number} outHeight The desired height of the output rasters
 * @param {number} samples The number of samples per pixel for pixel
 *                                 interleaved data
 * @param {string} [method = 'nearest'] The desired resampling method
 * @returns {TypedArray} The resampled rasters
 */

function resampleInterleaved(valueArray, inWidth, inHeight, outWidth, outHeight, samples) {
  var method = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : 'nearest';

  switch (method.toLowerCase()) {
    case 'nearest':
      return resampleNearestInterleaved(valueArray, inWidth, inHeight, outWidth, outHeight, samples);

    case 'bilinear':
    case 'linear':
      return resampleBilinearInterleaved(valueArray, inWidth, inHeight, outWidth, outHeight, samples);

    default:
      throw new Error("Unsupported resampling method: '".concat(method, "'"));
  }
}

exports.resampleInterleaved = resampleInterleaved;
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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